import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { LAND_CATEGORY_OPTIONS } from "web_myplann/Portfolio/Forms/LandForm/landCategoryOptions";
import type {
	LandFormInput,
	LandFormOutput,
} from "web_myplann/Portfolio/Forms/LandForm/landFormSchema";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowFormSwitch } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSwitch";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { UNITS } from "web_myplann/Shared/Utils/units";
import { createFieldGroup } from "web_myplann/Shared/Validation/fieldGroupDeps";

type LandFormFieldsProps = {
	control: Control<LandFormInput, unknown, LandFormOutput>;
};

const minimumGroup = createFieldGroup<LandFormInput>()([
	"location",
	"locationMunicipality",
	"landSize",
	"description",
]);

export const LandFormFields = ({ control }: LandFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const propertyCategoryOptions = useEnumSelectOptions(
		LAND_CATEGORY_OPTIONS,
		Resources.PortfolioDetail.Property.LandTypeEnum,
	);

	return (
		<>
			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddLand.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddLand.Fields.name)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddLand.Sections.location)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSelect
					control={control}
					name="propertyCategory"
					label={t(Resources.PortfolioDetail.Property.LandType)}
					options={propertyCategoryOptions}
				/>
				<FlatRowFormInput
					control={control}
					name="location"
					deps={minimumGroup.depsFor("location")}
					label={t(Resources.Portfolio.AddLand.Fields.location)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="locationMunicipality"
					deps={minimumGroup.depsFor("locationMunicipality")}
					label={t(Resources.Portfolio.AddLand.Fields.locationMunicipality)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="locationZipCode"
					label={t(Resources.Portfolio.AddLand.Fields.locationZipCode)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="parcelNumber"
					label={t(Resources.Portfolio.AddLand.Fields.parcelNumber)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddLand.Sections.valueAndDates)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="landSize"
					deps={minimumGroup.depsFor("landSize")}
					type="number"
					label={t(Resources.Portfolio.AddLand.Fields.landSize)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					suffix={UNITS.squareMeter}
				/>
				<FlatRowFormInput
					control={control}
					name="landValue"
					type="number"
					label={t(Resources.Portfolio.AddLand.Fields.value)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					suffix="Kč"
				/>
				<FlatRowFormDatePicker
					control={control}
					name="purchaseDate"
					label={t(Resources.Portfolio.AddLand.Fields.purchaseDate)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddLand.Sections.description)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="description"
					deps={minimumGroup.depsFor("description")}
					label={t(Resources.Portfolio.AddLand.Fields.description)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					rows={1}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddArt.Sections.investment)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="forInvestments"
					label={t(Resources.Portfolio.AddArt.Fields.forInvestments)}
					isLast
				/>
			</Stack>
		</>
	);
};
